import { useTranslation } from '@/hooks/useTranslation';
import { Check, Copy } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

/** Logical artwork size (matches previous 240×280) scaled +26.5% for display (1.15×1.1). */
const SCALE = 1.265;
/** Display width of the blue TnG voucher canvas — keep note box aligned to this. */
export const TNG_REWARD_CANVAS_WIDTH = Math.round(240 * SCALE);
const W = TNG_REWARD_CANVAS_WIDTH;
const H = Math.round(280 * SCALE);

/** `Tng.value` from DB may be `2.00` or `RM2.00` — always show as `RM …` in the white box. */
function formatTngValueForCanvas(raw: string | null | undefined): string {
  const v = (raw ?? '').trim();
  if (!v) return 'RM 2.00';
  const withoutPrefix = v.replace(/^RM\s*/i, '').trim();
  return withoutPrefix ? `RM ${withoutPrefix}` : 'RM 2.00';
}

/** DB/SMS often store `XXXXX XXXXX` — canvas + copy use continuous digits. */
function formatTngPinForCanvas(raw: string): string {
  return raw.replace(/\s+/g, '');
}

async function writeClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function useCopyPin(pin: string) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(id);
  }, [copied]);

  const handleCopy = () => {
    void writeClipboard(pin).then((ok) => {
      if (ok) setCopied(true);
    });
  };

  return { t, copied, handleCopy };
}

type Props = {
  pin: string;
  /** Raw `Tng.value` (e.g. `2.00`); displayed as `RM …` in the white area. */
  valueLabel?: string | null;
  expiryLabel?: string | null;
};

/**
 * Draws `/images/TnG.jpg` and overlays PIN / value / expiry like `ploom-cd/tng_canvas.js`
 * (value in white box with RM prefix, PIN centered, expiry to the right of the printed label).
 * A small copy-icon button sits just before the painted PIN (PIN drawing unchanged).
 */
export default function TngRewardCanvas({ pin, valueLabel, expiryLabel }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);
  const pinDisplay = formatTngPinForCanvas(pin);
  const { t, copied, handleCopy } = useCopyPin(pinDisplay);
  const [copyStyle, setCopyStyle] = useState<{ left: string; top: string } | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = W;
    canvas.height = H;

    const img = new Image();
    img.decoding = 'async';
    img.src = '/images/TnG.jpg';

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      ctx.drawImage(img, 0, 0, W, H);
      ctx.fillStyle = '#000';

      const rm = formatTngValueForCanvas(valueLabel);
      ctx.textAlign = 'center';
      ctx.font = `bold ${Math.round(16 * SCALE)}px Arial`;
      ctx.fillText(rm, W / 2, Math.round(200 * SCALE));

      ctx.font = `bold ${Math.round(20 * SCALE)}px Arial`;
      const pinX = W / 2;
      ctx.fillText(pinDisplay, pinX, Math.round(233 * SCALE));

      /** Copy control sits to the left of the centred PIN. */
      const pinHalf = ctx.measureText(pinDisplay).width / 2;
      const gap = 6 * SCALE;
      const btnWidth = 58;
      const nudgeRight = 12 * SCALE;
      const leftPx = pinX - pinHalf - gap - btnWidth + nudgeRight;
      /**
       * White PIN strip on artwork is ~y 356–398 of 469 → centre ≈ 80.4% of height.
       * Button uses -translate-y-1/2 so this is its vertical centre.
       */
      const pinBarCenterY = (377 / 469) * 100;
      setCopyStyle({
        left: `${(leftPx / W) * 100}%`,
        top: `${pinBarCenterY}%`,
      });

      if (expiryLabel?.trim()) {
        ctx.font = `bold ${Math.round(16 * SCALE)}px Arial`;
        ctx.textAlign = 'left';
        /** Just right of “PIN Expiry:” on the artwork (x tuned for alignment). */
        ctx.fillText(expiryLabel.trim(), Math.round(92 * SCALE), Math.round(262 * SCALE));
      }
    };

    img.onload = draw;
    if (img.complete) {
      draw();
    }
    img.onerror = () => {
      ctx.fillStyle = '#fff';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Could not load voucher image.', W / 2, Math.round(H / 2));
    };
  }, [pinDisplay, valueLabel, expiryLabel]);

  return (
    <div className="relative mx-auto w-full" style={{ maxWidth: W }}>
      <canvas
        ref={ref}
        width={W}
        height={H}
        className="mx-auto block h-auto w-full overflow-hidden rounded-2xl"
        style={{ maxWidth: W }}
        aria-label="Touch n Go eWallet reload PIN voucher"
      />
      {copyStyle != null ? (
        <button
          type="button"
          className="absolute z-10 flex h-[28px] -translate-y-1/2 items-center gap-1 text-slate-800 active:opacity-70"
          style={{ left: copyStyle.left, top: copyStyle.top }}
          onClick={handleCopy}
          aria-label={copied ? t.tng.copiedPin : t.tng.copyPin}
          title={copied ? t.tng.copiedPin : t.tng.copyPin}
        >
          {copied ? (
            <Check className="h-[16px] w-[16px] flex-shrink-0 text-emerald-600" strokeWidth={2.5} aria-hidden />
          ) : (
            <>
              <Copy className="h-[14px] w-[14px] flex-shrink-0" strokeWidth={2.25} aria-hidden />
              <span className="text-[11px] font-bold leading-none tracking-tight">
                {t.tng.copyLabel}
              </span>
            </>
          )}
        </button>
      ) : null}
    </div>
  );
}

/** Extra copy-PIN control for the note box below the canvas (aligns with first text line). */
export function TngCopyPinButton({ pin }: { pin: string }) {
  const { t, copied, handleCopy } = useCopyPin(formatTngPinForCanvas(pin));

  return (
    <button
      type="button"
      className="mt-0.5 flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center text-slate-800 active:opacity-70 sm:mt-1 sm:h-6 sm:w-6"
      onClick={handleCopy}
      aria-label={copied ? t.tng.copiedPin : t.tng.copyPin}
      title={copied ? t.tng.copiedPin : t.tng.copyPin}
    >
      {copied ? (
        <Check className="h-[20px] w-[20px] text-emerald-600 sm:h-[22px] sm:w-[22px]" strokeWidth={2.5} aria-hidden />
      ) : (
        <Copy className="h-[20px] w-[20px] sm:h-[22px] sm:w-[22px]" strokeWidth={2.25} aria-hidden />
      )}
    </button>
  );
}
